import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatInr, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STATES = ["Maharashtra", "Delhi", "Karnataka", "Tamil Nadu", "Telangana", "Gujarat", "Uttar Pradesh", "West Bengal", "Rajasthan", "Kerala", "Punjab", "Haryana", "Madhya Pradesh", "Andhra Pradesh", "Bihar", "Odisha", "Assam", "Other"];

export default function Checkout() {
  const { items, subtotal, gst, shipping, total, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: user?.name || "",
    phone: user?.phone || "",
    clinic_name: "",
    line1: "", line2: "", city: "", state: "Maharashtra", pincode: "",
  });
  const [method, setMethod] = useState("cod");
  const [notes, setNotes] = useState("");

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="font-display text-3xl">Your cart is empty</h1>
        <button onClick={() => navigate("/products")} className="fm-btn-primary mt-6">Browse catalogue</button>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        address: form,
        payment_method: method,
        notes,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      toast.success(`Order ${data.id.slice(0, 8)} placed!`);
      navigate("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-12 gap-10">
      <form onSubmit={submit} className="lg:col-span-8 space-y-10">
        <div>
          <div className="overline">Checkout</div>
          <h1 className="font-display text-4xl mt-2">Delivery details</h1>
        </div>

        <section className="space-y-4">
          <div className="overline">Shipping address</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label="Full name *" required value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="full_name" />
            <Input label="Phone *" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="phone" />
            <Input label="Clinic name" value={form.clinic_name} onChange={(v) => setForm({ ...form, clinic_name: v })} testid="clinic_name" />
            <div />
            <Input label="Address line 1 *" required value={form.line1} onChange={(v) => setForm({ ...form, line1: v })} testid="line1" className="sm:col-span-2" />
            <Input label="Address line 2" value={form.line2} onChange={(v) => setForm({ ...form, line2: v })} testid="line2" className="sm:col-span-2" />
            <Input label="City *" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="city" />
            <div>
              <label className="overline">State *</label>
              <select required data-testid="state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--fm-primary)]">
                {STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <Input label="Pincode *" required value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="pincode" />
          </div>
        </section>

        <section className="space-y-4">
          <div className="overline">Payment method</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { id: "cod", t: "Pay on delivery", d: "Cash or bank transfer at delivery." },
              { id: "online", t: "Online (invoice link)", d: "Receive a secure payment link by email." },
            ].map((m) => (
              <label key={m.id} data-testid={`pay-${m.id}`} className={`border p-4 cursor-pointer ${method === m.id ? "border-[var(--fm-primary)] bg-[var(--fm-sage)]" : "border-[var(--fm-border)] bg-white"}`}>
                <input type="radio" name="pay" className="mr-2 accent-[var(--fm-primary)]" checked={method === m.id} onChange={() => setMethod(m.id)} />
                <span className="font-medium">{m.t}</span>
                <div className="text-xs text-[var(--fm-text-2)] mt-1 ml-6">{m.d}</div>
              </label>
            ))}
          </div>
        </section>

        <section>
          <div className="overline">Order notes (optional)</div>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="order-notes" className="w-full border border-[var(--fm-border)] px-3 py-2.5 text-sm mt-2 focus:outline-none focus:border-[var(--fm-primary)]" placeholder="Installation requirements, preferred delivery slot, etc." />
        </section>
      </form>

      <aside className="lg:col-span-4">
        <div className="bg-white border border-[var(--fm-border)] p-7 lg:sticky lg:top-28">
          <div className="overline">Order summary</div>
          <div className="mt-5 space-y-3 text-sm">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between text-[var(--fm-text-2)]">
                <span className="line-clamp-1 max-w-[60%]">{i.name} × {i.quantity}</span>
                <span className="font-mono-fm">{formatInr(i.price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--fm-border)] my-4" />
          <div className="space-y-2 text-sm">
            <Row label="Subtotal" value={formatInr(subtotal)} />
            <Row label="GST (18%)" value={formatInr(gst)} />
            <Row label="Shipping" value={shipping === 0 ? "Free" : formatInr(shipping)} />
            <div className="border-t border-[var(--fm-border)] pt-3 mt-3 flex justify-between text-base">
              <span className="font-medium">Total</span>
              <span className="font-mono-fm font-semibold text-[var(--fm-primary)]">{formatInr(total)}</span>
            </div>
          </div>
          <button onClick={submit} disabled={busy} data-testid="place-order-btn" className="fm-btn-primary w-full mt-6">{busy ? "Placing order…" : "Place order"}</button>
        </div>
      </aside>
    </div>
  );
}

function Input({ label, value, onChange, testid, required, className = "" }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input required={required} value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--fm-primary)]" />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[var(--fm-text-2)]">
      <span>{label}</span><span className="font-mono-fm">{value}</span>
    </div>
  );
}
